import { compact } from 'lodash-es';

import { VIPMembershipBenefit } from './types';

type Props = {
  isPremiumFilterEnabled: boolean;
  showFullProfileAccess: boolean;
  defaultDailyVIPLimitBalance: number;
  defaultVIPShortlistQuota?: number;
  isMonthlyQuotaEnabled?: boolean;
};

export const vipBenefitsPopoverPromotionImages = {
  requiredApplicationFields: {
    id: '/images/vip-benefits/required-application-fields-id.png',
    en: '/images/vip-benefits/required-application-fields-en.png',
    'en-my': '/images/vip-benefits/required-application-fields-en-my.png',
    'en-sg': '/images/vip-benefits/required-application-fields-en-sg.png',
  },
  mandatoryResumeUpload: {
    id: '/images/vip-benefits/mandatory-resume-upload-id.png',
    en: '/images/vip-benefits/mandatory-resume-upload-en.png',
  },
  hideSalary: {
    id: '/images/vip-benefits/hide-salary-id.png',
    en: '/images/vip-benefits/hide-salary-en.png',
    'en-my': '/images/vip-benefits/hide-salary-en-my.png',
    'en-sg': '/images/vip-benefits/hide-salary-en-sg.png',
  },
  premiumFilters: {
    id: '/images/vip-benefits/premium-filters-id.png',
    en: '/images/vip-benefits/premium-filters-en.png',
  },
  highlightedJobCard: {
    id: '/images/vip-benefits/highlighted-job-card-id.png',
    en: '/images/vip-benefits/highlighted-job-card-en.png',
    'en-my': '/images/vip-benefits/highlighted-job-card-en-my.png',
    'en-sg': '/images/vip-benefits/highlighted-job-card-en-sg.png',
  },
  fullProfileAccess: {
    id: '/images/vip-benefits/full-profile-access-id.png',
    en: '/images/vip-benefits/full-profile-access-en.png',
  },
};

const getPremiumFilterDescription = ({
  isPremiumFilterEnabled,
}: Pick<Props, 'isPremiumFilterEnabled'>) => {
  if (isPremiumFilterEnabled) {
    return {
      description: {
        id: 'text-elevate-your-hiring-with.2',
        defaultMessage:
          'Elevate your hiring with premium filters to swiftly identify top candidates',
      },
      promotionImages: vipBenefitsPopoverPromotionImages.premiumFilters,
    } as const;
  }

  return {
    description: {
      id: 'text-coming-soon',
      defaultMessage: 'Coming Soon',
    },
    isComingSoon: true,
  } as const;
};

export const getVIPMembershipBenefits = ({
  isPremiumFilterEnabled,
  showFullProfileAccess,
  defaultDailyVIPLimitBalance,
  defaultVIPShortlistQuota,
  isMonthlyQuotaEnabled,
}: Props): VIPMembershipBenefit[] =>
  compact([
    showFullProfileAccess &&
      (!isMonthlyQuotaEnabled ||
        typeof defaultVIPShortlistQuota === 'number') && {
        imageSrc: '/images/credit-system/vip-benefits/full-profile-access.svg',
        heading: isMonthlyQuotaEnabled
          ? {
              id: 'text-count-shortlist-quota',
              defaultMessage: '{count} Shortlists Quota',
              values: {
                count: defaultVIPShortlistQuota,
              },
            }
          : {
              id: 'text-count-shortlist',
              defaultMessage: '{count} Shortlists* per Day',
              values: {
                count: defaultDailyVIPLimitBalance,
              },
            },
        description: {
          id: 'text-enjoy-unlimited-viewing-of',
          defaultMessage:
            'Enjoy unlimited viewing of all candidate profiles who apply to your job posts.',
        },
        ...(isMonthlyQuotaEnabled
          ? {}
          : {
              promotionImages:
                vipBenefitsPopoverPromotionImages.fullProfileAccess,
            }),
      },
    {
      imageSrc:
        '/images/credit-system/vip-benefits/required-application-fields.svg',
      heading: {
        id: 'text-required-application-fields',
        defaultMessage: 'Required Application Fields',
      },
      description: {
        id: 'text-mark-certain-fields-to',
        defaultMessage:
          'Mark certain fields to be required to submit when candidates apply for your jobs.',
      },
      promotionImages:
        vipBenefitsPopoverPromotionImages.requiredApplicationFields,
    },
    {
      imageSrc:
        '/images/credit-system/vip-benefits/mandatory-resume-upload.svg',
      heading: {
        id: 'text-mandatory-resume-upload',
        defaultMessage: 'Mandatory Resume Upload',
      },
      description: {
        id: 'text-candidates-will-be-required',
        defaultMessage:
          'Candidates will be required to upload their resumes when they apply for your jobs.',
      },
      promotionImages: vipBenefitsPopoverPromotionImages.mandatoryResumeUpload,
    },
    {
      imageSrc: '/images/credit-system/vip-benefits/highlighted-job-card.svg',
      heading: {
        id: 'text-highlighted-job-card',
        defaultMessage: 'Highlighted job card',
      },
      description: {
        id: 'text-stand-out-among-the',
        defaultMessage:
          'Stand out among the rest with a VIP exclusive job card design and badge next to your company name.',
      },
      promotionImages: vipBenefitsPopoverPromotionImages.highlightedJobCard,
    },
    {
      imageSrc: '/images/credit-system/vip-benefits/hide-salary.svg',
      heading: {
        id: 'text-hide-salary',
        defaultMessage: 'Hide Salary',
      },
      description: {
        id: 'text-do-not-show-your',
        defaultMessage:
          'Do not show your salary to job seekers that view your job post.',
      },
      promotionImages: vipBenefitsPopoverPromotionImages.hideSalary,
    },
    {
      imageSrc: '/images/credit-system/vip-benefits/premium-filters.svg',
      heading: {
        id: 'text-premium-filters',
        defaultMessage: 'Premium Filters',
      },
      ...getPremiumFilterDescription({ isPremiumFilterEnabled }),
    },
    {
      imageSrc: '/images/credit-system/vip-benefits/auto-invite-candidates.svg',
      heading: {
        id: 'text-auto-invite-candidates',
        defaultMessage: 'Auto-Invite Candidates',
      },
      description: {
        id: 'text-reach-top-candidates-faster',
        defaultMessage:
          'Reach top candidates faster by automatically inviting your best matches to apply.',
      },
    },
  ]);

export const vipMembershipBenefitsWithFPAL: VIPMembershipBenefit[] = compact([
  {
    imageSrc: 'images/vip-benefits/ri-file-edit.svg',
    heading: {
      id: 'text-required-application-fields',
      defaultMessage: 'Required Application Fields',
    },
    description: {
      id: 'text-mark-certain-fields-to',
      defaultMessage:
        'Mark certain fields to be required to submit when candidates apply for your jobs.',
    },
    promotionImages:
      vipBenefitsPopoverPromotionImages.requiredApplicationFields,
  },
  {
    imageSrc: 'images/vip-benefits/ri-bank-card.svg',
    heading: {
      id: 'text-highlighted-job-card',
      defaultMessage: 'Highlighted job card',
    },
    description: {
      id: 'text-stand-out-among-the',
      defaultMessage:
        'Stand out among the rest with a VIP exclusive job card design and badge next to your company name.',
    },
    promotionImages: vipBenefitsPopoverPromotionImages.highlightedJobCard,
  },
  {
    imageSrc: 'images/vip-benefits/ri-file-list.svg',
    heading: {
      id: 'text-mandatory-resume-upload',
      defaultMessage: 'Mandatory Resume Upload',
    },
    description: {
      id: 'text-candidates-will-be-required',
      defaultMessage:
        'Candidates will be required to upload their resumes when they apply for your jobs.',
    },
    promotionImages: vipBenefitsPopoverPromotionImages.mandatoryResumeUpload,
  },
  {
    imageSrc: 'images/vip-benefits/ri-money-dollar-circle.svg',
    heading: {
      id: 'text-hide-salary',
      defaultMessage: 'Hide Salary',
    },
    description: {
      id: 'text-do-not-show-your',
      defaultMessage:
        'Do not show your salary to job seekers that view your job post.',
    },
    promotionImages: vipBenefitsPopoverPromotionImages.hideSalary,
  },
  {
    imageSrc: 'images/vip-benefits/ri-filter-2.svg',
    heading: {
      id: 'text-premium-filters',
      defaultMessage: 'Premium Filters',
    },
    ...getPremiumFilterDescription({ isPremiumFilterEnabled: true }),
  },
  {
    imageSrc: 'images/vip-benefits/ri-user-add-line.svg',
    heading: {
      id: 'text-auto-invite-candidates',
      defaultMessage: 'Auto-Invite Candidates',
    },
    description: {
      id: 'text-reach-top-candidates-faster',
      defaultMessage:
        'Reach top candidates faster by automatically inviting your best matches to apply.',
    },
  },
]);
